import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Icon,
  Input,
  Select,
  Textarea,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import { FiSend } from "react-icons/fi";

const MotionBox = motion(Box);

interface ContactFormFields {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

export const ContactForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ContactFormFields>();

  const cardBg = useColorModeValue("white", "whiteAlpha.50");
  const textColor = useColorModeValue("gray.800", "white");
  const inputBg = useColorModeValue("white", "dark.900");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const onSubmit = async (_data: ContactFormFields) => {
    setIsSubmitting(true);
    await new Promise((resolve) => setTimeout(resolve, 1500));
    toast({
      title: "درخواست شما ثبت شد",
      description: "همکاران ما در اسرع وقت با شما تماس خواهند گرفت.",
      status: "success",
      duration: 5000,
      isClosable: true,
      position: "top",
    });
    reset();
    setIsSubmitting(false);
  };

  return (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 } as any}
    >
      <Box
        bg={cardBg}
        p={{ base: 6, md: 10 }}
        borderRadius="3xl"
        shadow="sm"
        border="1px solid"
        borderColor={borderColor}
      >
        <VStack spacing={5} align="stretch">
          <Heading
            as="h2"
            fontSize={{ base: "2xl", md: "2xl" }}
            color={textColor}
            mb={8}
          >
            فرم ثبت درخواست و مکاتبات
          </Heading>

          <form onSubmit={handleSubmit(onSubmit)}>
            <VStack spacing={5} align="stretch">
              <Box
                display={{ base: "block", md: "grid" }}
                gridTemplateColumns={{ md: "repeat(2, minmax(0, 1fr))" }}
                gap={4}
              >
                <FormControl isInvalid={!!errors.name}>
                  <FormLabel color={textColor} fontSize="sm">
                    نام و نام خانوادگی
                  </FormLabel>
                  <Input
                    {...register("name", {
                      required: "نام و نام خانوادگی الزامی است",
                    })}
                    placeholder="نام خود را وارد کنید"
                    bg={inputBg}
                    borderColor={borderColor}
                    size="lg"
                    _hover={{ borderColor: "brand.500" }}
                    _focus={{
                      borderColor: "brand.500",
                      boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)",
                    }}
                  />
                  <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
                </FormControl>

                <FormControl mt={{ base: 4, md: 0 }}>
                  <FormLabel color={textColor} fontSize="sm">
                    شماره تماس
                  </FormLabel>
                  <Input
                    {...register("phone")}
                    placeholder="۰۹۱۲۳۴۵۶۷۸۹"
                    bg={inputBg}
                    borderColor={borderColor}
                    size="lg"
                    _hover={{ borderColor: "brand.500" }}
                    _focus={{
                      borderColor: "brand.500",
                      boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)",
                    }}
                  />
                </FormControl>
              </Box>

              <Box
                display={{ base: "block", md: "grid" }}
                gridTemplateColumns={{ md: "repeat(2, minmax(0, 1fr))" }}
                gap={4}
              >
                <FormControl isInvalid={!!errors.email}>
                  <FormLabel color={textColor} fontSize="sm">
                    ایمیل
                  </FormLabel>
                  <Input
                    {...register("email", {
                      required: "ایمیل الزامی است",
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "ایمیل نامعتبر است",
                      },
                    })}
                    type="email"
                    placeholder="example@email.com"
                    bg={inputBg}
                    borderColor={borderColor}
                    size="lg"
                    _hover={{ borderColor: "brand.500" }}
                    _focus={{
                      borderColor: "brand.500",
                      boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)",
                    }}
                  />
                  <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
                </FormControl>

                <FormControl
                  isInvalid={!!errors.subject}
                  mt={{ base: 4, md: 0 }}
                >
                  <FormLabel color={textColor} fontSize="sm">
                    موضوع
                  </FormLabel>
                  <Select
                    {...register("subject", {
                      required: "موضوع الزامی است",
                    })}
                    placeholder="انتخاب موضوع"
                    bg={inputBg}
                    borderColor={borderColor}
                    size="lg"
                    _hover={{ borderColor: "brand.500" }}
                    _focus={{
                      borderColor: "brand.500",
                      boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)",
                    }}
                  >
                    <option value="investment">مشاوره سرمایه‌گذاری</option>
                    <option value="funds">صندوق‌های سرمایه‌گذاری</option>
                    <option value="services">خدمات تأمین مالی</option>
                    <option value="complaint">شکایات و پیشنهادات</option>
                    <option value="other">سایر موارد</option>
                  </Select>
                  <FormErrorMessage>{errors.subject?.message}</FormErrorMessage>
                </FormControl>
              </Box>

              <FormControl isInvalid={!!errors.message}>
                <FormLabel color={textColor} fontSize="sm">
                  پیام
                </FormLabel>
                <Textarea
                  {...register("message", {
                    required: "پیام الزامی است",
                  })}
                  placeholder="جزئیات درخواست خود را بنویسید..."
                  rows={7}
                  bg={inputBg}
                  borderColor={borderColor}
                  resize="vertical"
                  _hover={{ borderColor: "brand.500" }}
                  _focus={{
                    borderColor: "brand.500",
                    boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)",
                  }}
                />
                <FormErrorMessage>{errors.message?.message}</FormErrorMessage>
              </FormControl>

              <Button
                type="submit"
                colorScheme="brand"
                size="lg"
                isLoading={isSubmitting}
                loadingText="در حال ارسال..."
                rightIcon={<Icon verticalAlign="middle" as={FiSend as any} />}
                w="100%"
                shadow="lg"
                _hover={{
                  transform: "translateY(-2px)",
                  shadow: "xl",
                }}
                transition="all 0.3s"
              >
                ارسال درخواست
              </Button>
            </VStack>
          </form>
        </VStack>
      </Box>
    </MotionBox>
  );
};
